"use client";

import React, { useContext, useEffect, useState } from "react";
import { isHTML, parseTextWithEnviromentVariables } from "@/utils";
import sanitizeHTML from "@/utils/sanitizeHtml";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, MARKS } from "@contentful/rich-text-types";
import { GlobalContext } from "@/contexts/SiteDataContext";
import PhotoRequirements from "@/components/molecules/PhotoRequirements";
import TableToggle from "@/components/organisms/TableToggle";
import { Params } from "@dictionaries/i18";
import "./styles.scss";

function containsTableTag(htmlString: string) {
  return /<table[^>]*>/i.test(htmlString);
}

function containsHtmlTagsAnywhere(str: string) {
  const regex = /<[^>]+>/g;
  return regex.test(str);
}

// New way to translate richtext content
type TFunc = (key: string, params?: Params) => string;

// Old way to translate richtext content
type TMap = { [key: string]: string };

interface RichtextProps {
  t?: TMap | TFunc;
  content: any;
  allowTranslations?: boolean;
  customClass?: string;
}

interface TextProps {
  children: React.ReactNode[];
}

const Richtext = ({ content, t, allowTranslations, customClass }: RichtextProps) => {
  const siteDataComplete = useContext(GlobalContext).context.siteDataComplete;

  // TODO Remove from this after contentful migration is done
  const Bold = ({ children }: { children: any }) => <b>{children}</b>;

  const Text: React.FC<TextProps> = ({ children }) => {
    const parsedChildren = React.Children.map(children, (child) => {
      if (React.isValidElement(child) && child.type === "span") {
        const { children: spanChildren, key } = child.props;

        if (containsTableTag(spanChildren)) {
          return false;
        }

        if (containsHtmlTagsAnywhere(spanChildren)) {
          return (
            <span
              key={key}
              dangerouslySetInnerHTML={{ __html: spanChildren }}
            />
          );
        }
      } else if (typeof child === "string") {
        if (containsTableTag(child)) {
          return (
            <div className="richText__tableContainer">
              <span
                key={child}
                dangerouslySetInnerHTML={{ __html: child }}
              />
            </div>
          );
        }
        if (containsHtmlTagsAnywhere(child)) {
          const translatedChild =
            allowTranslations && t ? (typeof t === "function" ? t(child) : t[child]) : child;
          return (
            <span
              key={child}
              dangerouslySetInnerHTML={{ __html: translatedChild }}
            />
          );
        }
        return allowTranslations && t ? (typeof t === "function" ? t(child) : t[child]) : child;
      }

      return child;
    });

    return <div className="p">{parsedChildren}</div>;
  };

  const options = {
    renderMark: {
      [MARKS.BOLD]: (text: any) => <Bold>{text}</Bold>,
      [MARKS.CODE]: (code: any) => {
        if (isHTML(code)) {
          return <span dangerouslySetInnerHTML={{ __html: code }} />;
        }

        const content =
          typeof code === "object" ? siteDataComplete[code.props.children] : siteDataComplete[code];

        return <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(content) }}></span>;
      },
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
        return <Text>{children}</Text>;
      },
      [BLOCKS.HEADING_2]: (node: any, children: any) => {
        return (
          <h2>
            {children.map((x: any) =>
              typeof x === "string" && t ? (typeof t === "function" ? t(x) : t[x]) : x
            )}
          </h2>
        );
      },
      [BLOCKS.HEADING_5]: (node: any, children: any) => {
        return (
          <div className="r-alert">
            <p>{children}</p>
          </div>
        );
      },
      [BLOCKS.QUOTE]: (node: any, children: any) => {
        return (
          <div className="m-blockquote">
            <PhotoRequirements />
          </div>
        );
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
        return (
          <TableToggle
            title={node.data.target.fields.title}
            content={node.data.target.fields.table && node.data.target.fields.table}
            richtext={node.data.target.fields.content && node.data.target.fields.content}
          />
        );
      },
    },
    renderText: (text: any) => text,
  };

  return (
    <div className={`richText u-editor ${customClass || ""}`.trim()}>
      {documentToReactComponents(content, options)}
    </div>
  );
};

export default Richtext;
